import { useState } from "react";
import { Link } from "react-router-dom";
import { Video } from "lucide-react";
import { SidebarMenuItem, SidebarMenuButton } from "@/components/ui/sidebar";
import { MenuTutorialVideoModal } from "./MenuTutorialVideoModal";
import { useMenuTutorialVideos } from "@/hooks/useMenuTutorialVideos";

interface SettingsMenuItemWithTutorialProps {
  title: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  tutorialVideo?: { videoUrl: string; title: string; lessonId?: string };
}

export function SettingsMenuItemWithTutorial({
  title,
  path,
  icon: Icon,
  isActive,
  tutorialVideo,
}: SettingsMenuItemWithTutorialProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showTutorialIcons, isWatched, markAsWatched } = useMenuTutorialVideos();

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleWatched = () => {
    markAsWatched.mutate({ menuItemId: path, sidebarType: 'settings' });
  };

  // Determine if we should show the video icon
  const hasVideo = !!tutorialVideo;
  const alreadyWatched = isWatched(path, 'settings');
  const shouldShowIcon = hasVideo && showTutorialIcons && !alreadyWatched;

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton 
          asChild 
          isActive={isActive}
          tooltip={title}
        >
          <Link to={path} className="w-full group">
            <Icon className="h-4 w-4" />
            <span className="flex-1">{title}</span>
            {shouldShowIcon && (
              <button
                onClick={handlePlayClick}
                className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto p-1 hover:bg-primary/10 rounded"
                title="Watch tutorial"
              >
                <Video className="h-3 w-3 text-primary" />
              </button>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>

      {tutorialVideo && (
        <MenuTutorialVideoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          videoUrl={tutorialVideo.videoUrl}
          title={tutorialVideo.title}
          lessonId={tutorialVideo.lessonId}
          menuItemLabel={title}
          onWatched={handleWatched}
        />
      )}
    </>
  );
}
